<template>
  <form @submit.prevent>
    <div class="form">
      <CurrencyInput
        v-if="fiatInputs"
        key="buyFiat"
        name="fiat"
        type="text"
        label="Spend"
        placeholder="Fiat amount"
        :defaultValue="{
          fiatAmount: form.fiatAmount,
          fiatType: form.fiatType,
        }"
        :items="fiatInputs"
        :isLoading="isFetchingData.fiatType"
        :errors="store.getErrorByField('fiatAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <CurrencyInput
        v-if="coinInputs"
        key="buyCoin"
        name="coin"
        type="text"
        label="Receive"
        placeholder="Coin amount"
        :defaultValue="{
          coinAmount: form.coinAmount,
          coinType: form.coinType,
        }"
        :items="coinInputs"
        :isLoading="isFetchingData.coinType"
        :errors="store.getErrorByField('coinAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <!-- todo  he-452 to add loading state prop-->
      <WalletAddressField
        v-if="
          store.form.walletAddress.fromApi === false ||
          globalStore.getMerchantSettings?.readOnlyWalletAddress === true ||
          globalStore.getMerchantSettings?.referredWalletAddressIsEditable ===
            true
        "
        :isReadOnly="
          orderStore.isOrderFromMerchantApi &&
          !!globalStore.getMerchantSettings?.referredWalletAddressIsEditable ===
            false
        "
        :name="'walletAddress'"
        v-model:walletAddress="form.walletAddress"
        :errors="store.getErrorByField('walletAddress')"
      />

      <!-- todo  he-452 to add loading state prop-->
      <FormSelect
        type="text"
        name="blockchain"
        label="Selected blockchain"
        :key="'blockchain'"
        :disabled="disabledBlockchainSelect"
        :items="blockchainsByCoin"
        :defaultItem="defaultBlockchain"
        :isLoading="isFetchingData.blockchain"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormInputField
        v-if="walletTagByBlockchainData"
        type="'text'"
        :name="'walletAddressTag'"
        :label="`Your wallet ${walletTagByBlockchainData.name.toLowerCase()}`"
        :placeholder="`Enter your wallet ${walletTagByBlockchainData.name.toLowerCase()}`"
        :errors="store.getErrorByField('walletAddressTag')"
        v-model:walletAddressTag="form.walletAddressTag"
        :inputValue="form.walletAddressTag"
      />

      <PaymentInput
        key="buyPayments"
        :items="orderStore.filterPaymentsByFiatAndCoin"
        @paymentSelected="updateDropdown"
      />

      <OrderSummary :feeData="store.feeData" :formData="store.form" />

      <ButtonBase
        :isDisabled="
          store.formHasErrors ||
          globalStore.getIsFormLoading ||
          store.feeData.isLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Create Order
      </ButtonBase>
      <p class="term-use" v-if="initialData?.termOfUse?.show">{{ initialData?.termOfUse?.message }}</p>
    </div>
  </form>
</template>

<script setup>
// ====================================
// Packages
// ====================================
import {
  ref,
  watch,
  computed,
  provide,
  onBeforeMount,
  onBeforeUnmount,
} from "vue";
import { useRouter } from "vue-router";
import { useDebounceFn, whenever } from "@vueuse/core";
// ====================================
// Storage
// ====================================
import { useGlobalStore } from "@/stores/globalStore";
import { useBuyOrderFormStore } from "@/stores/forms/buyOrderFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
// ====================================
// Composables
// ====================================
import useApiHelpers from "@/composables/api/helpers";
import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useWalletAddressEventValidator from "@/composables/watchers/useWalletAddressEventValidator";
import useTransactionalLimits from "@/composables/watchers/useTransactionalLimits";
import useWalletTagByBlockchain from "@/composables/watchers/useWalletTagByBlockchain";
import useOrderCreation from "@/composables/api/useOrderCreation";
import useOrderForm from "@/composables/shared/useOrderForm";
import { toCurrencyFormat } from "@/composables/helpers/useStringHelpers";

// ====================================
// Components
// ====================================
import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import CurrencyInput from "@/components/ui/inputs/CurrencyInput.vue";
import FormSelect from "@/components/ui/selects/FormSelect.vue";
import PaymentInput from "@/components/ui/inputs/PaymentInput.vue";
import TwoColumnTable from "@/components/ui/tables/TwoColumnTable.vue";
import OrderSummary from "@/components/ui/tables/OrderSummary.vue";
import WalletAddressField from "@/components/ui/inputs/WalletAddressField.vue";
import FormInputField from "@/components/ui/inputs/FormInputField.vue";
import useWalletModal from "@/composables/ui/useWalletModal";

const router = useRouter();
const { postRequest } = useApiHelpers();
const { getFiats, getCoins, initOrder } = useOrderEndpoints();
const { watchFormAndUpdateStore } = useWatchAndUpdate();
const { watchWalletAddressAndHandleEvents } = useWalletAddressEventValidator();
const { watchAndHandleFiatValidation, watchAndHandleCoinValidation } =
  useTransactionalLimits();
const { watchWalletTagFieldByBlockchain } = useWalletTagByBlockchain();
const { dynamicPricingCalculator } = useDynamicPricingCalculator();
const { setFormRefsAndUpdateStore } = useOrderCreation();
const { findBlockchainsByCoin } = useOrderForm();

const orderStore = useOrderFormStore();
const store = useBuyOrderFormStore();
const { handleWalletModalFlow } = useWalletModal();
const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();

const props = defineProps({
  initialData: {
    type: Object,
    default: () => {},
  },
});
const fiatResponse = ref([]);
const fiatInputs = ref([]);
const coinResponse = ref([]);
const coinInputs = ref([]);
const blockchainsByCoin = ref([]);
const defaultBlockchain = ref("");
const form = ref({
  fiatType: "",
});
const provideBuyUiDisable = ref({});
const isFetchingData = ref({
  fiatAmount: true,
  coinAmount: true,
});
const setDefaultsFromUrl = ref(false);

const findAllBlockchainCodesByCoins = computed(() =>
  coinResponse.value?.data?.data?.coins
    .find((el) => el.coin_code === form.value.coinType)
    .blockchains.map((el) => {
      return {
        name: el.description,
        value: el.code,
      };
    })
);

const walletTagByBlockchainData = computed(
  () => store.getWalletMemoByBlockchain
);

const isWalletAddressChanged = ref(false);

const disabledBlockchainSelect = computed(() => {
  if (
    blockchainsByCoin.value &&
    blockchainsByCoin.value.includes("No valid blockchains available")
  ) {
    return true;
  }

  return false;
});

const handleFormSuccess = () => {
  postRequest("/orders/buy", store.getFormPayload, async (res) => {
    if (res.data.rel.route === "account") {
      router.push("/verification");
    }
    if (res.data.rel.route === "authentication") {
      router.push("/verification/code");
    }
    if (res.data.rel.route === "prepaid" || res.data.rel.route === "postpaid") {
      await identityQueueStore.fetchIdentityDocuments();
      router.push(identityQueueStore.getNextIdentityRoute);
    }
  });
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);

  // don't refresh session if from API
  if (!orderStore.isOrderFromMerchantApi) {
    await initOrder(); // clears the session
  }

  // skip wallet flow if not required by merchant
  if (
    store.$state.form.walletAddress.fromApi &&
    globalStore.getMerchantSettings?.referredWalletAddressIsEditable
  ) {
    handleWalletModalFlow(handleFormSuccess);
  } else {
    handleFormSuccess();
  }
};

const setDefaultsByLocalStorage = () => {
  Object.keys(store.form).forEach((key) => {
    if (key === "fiatType") {
      const defaultFiat =
        fiatInputs.value.indexOf(orderStore.settings.defaultFiatType) >= 0
          ? orderStore.settings.defaultFiatType
          : fiatInputs.value[0];

      form.value.fiatType = defaultFiat;
    } else if (key === "coinType") {
      const defaultCoin =
        coinInputs.value.indexOf(orderStore.settings.defaultCoinType) >= 0
          ? orderStore.settings.defaultCoinType
          : coinInputs.value[0];

      form.value.coinType = defaultCoin;
    } else {
      form.value[key] = store.form[key].value;
    }
  });

  const lockFiat = store.form.lockFiat.value;
  const fieldToTriggerCalcBy = lockFiat ? "fiatAmount" : "coinAmount";
  dynamicPricingCalculator(
    fieldToTriggerCalcBy,
    form.value[fieldToTriggerCalcBy],
    form,
    store
  );
};

const updateCurrencyInput = (inputData) => {
  const field = Object.keys(inputData)[0];
  const value = Object.values(inputData)[0];
  const lockFiat = field === "fiatAmount";

  form.value[field] = value;
  form.value.lockFiat = lockFiat;

  dynamicPricingCalculator(field, value, form, store);
};

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  if (value) form.value[field] = value;
  if (!isWalletAddressChanged.value) {
    dynamicPricingCalculator(field, value, form, store);
  }
  isWalletAddressChanged.value = false;
};

const changeLoadingValueByName = (name, isLoading) => {
  isFetchingData.value[name] = isLoading;
};

const runOnMounted = async () => {
  fiatResponse.value = await getFiats("buy");
  coinResponse.value = await getCoins("buy");

  fiatInputs.value = fiatResponse.value.data.data.fiats.map((el) => {
    return el.fiat_code;
  });

  coinInputs.value = coinResponse.value.data.data.coins.map(
    (el) => el.coin_code
  );

  // todo change to order store actiopn
  store.setCoinData(coinResponse.value.data.data.coins);

  // swift the stop loading here to improve the UX
  changeLoadingValueByName("fiatType", false);
  changeLoadingValueByName("coinType", false);
};

provide("isDisabledByKey", provideBuyUiDisable);

const addResizeEventListener = useDebounceFn(() => {
  if (globalStore.getIsFormLoading) {
    return;
  }
  if (
    store.$state.form.walletAddress.fromApi &&
    globalStore.getMerchantSettings?.referredWalletAddressIsEditable
  ) {
    handleWalletModalFlow(handleFormSuccess);
  }
}, 250);

onBeforeMount(() => {
  window.addEventListener("resize", addResizeEventListener);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", addResizeEventListener);
});

watch(
  () => form.value.coinType,
  () => {
    store.setCoinType(form.value.coinType);
    findBlockchainsByCoin(store, form, defaultBlockchain, blockchainsByCoin);
  },
  { deep: true }
);

watch(
  () => form.value.blockchain,
  (selectedBlockchain) => {
    store.triggerWalletValidation(form.value.walletAddress, selectedBlockchain);
  },
  { deep: true }
);

watch(
  () => globalStore.$state.token,
  (val) => {
    if (val) {
      runOnMounted();
    }
  },
  { deep: true, immediate: true }
);

// when coin and fiat api calls are done + props are set from OrderView
// call merchApi
whenever(
  () =>
    fiatInputs.value.length > 0 &&
    coinInputs.value.length > 0 &&
    typeof props.initialData.areDefaultsFromUrl === "boolean",
  () => {
    if (!props.initialData.areDefaultsFromUrl) {
      setDefaultsByLocalStorage();
      return;
    }

    setFormRefsAndUpdateStore(
      props.initialData.urlData,
      form,
      store,
      fiatInputs.value,
      coinInputs.value
    );

    if (
      props.initialData.disableUIKeys &&
      props.initialData.orderMode === "BUY"
    ) {
      provideBuyUiDisable.value = props.initialData.disableUIKeys;
    }
  },
  { deep: true }
);

// if merch api data is redirecting to next page and we need to show the wallet modal
whenever(
  () =>
    typeof props.initialData.areDefaultsFromUrl === "boolean" &&
    orderStore.canSkipOrderPageFromApiData &&
    store.$state.form.walletAddress.fromApi &&
    globalStore.getMerchantSettings?.referredWalletAddressIsEditable,
  () => {
    handleWalletModalFlow(handleFormSuccess);
  },
  { deep: true }
);

watchFormAndUpdateStore(form, store, () => {});
watchWalletAddressAndHandleEvents(
  form,
  store,
  findAllBlockchainCodesByCoins,
  (filteredBlockchains) => {
    // if the selected coin also has the selected blockchain, don't update the value
    const selectedBlockchain = form.value.blockchain;
    const selectedBlockchainIsAPartOfValidatedBlockchains =
      filteredBlockchains.find(
        (blockchain) => blockchain.value === selectedBlockchain
      );

    if (selectedBlockchainIsAPartOfValidatedBlockchains) {
      defaultBlockchain.value = selectedBlockchain;
    } else {
      defaultBlockchain.value = "";
    }
    blockchainsByCoin.value = filteredBlockchains;
    isWalletAddressChanged.value = form.value.walletAddress !== null;
  }
);
watchAndHandleFiatValidation(form, store);
watchAndHandleCoinValidation(form, store);
watchWalletTagFieldByBlockchain(walletTagByBlockchainData, store);
</script>

<style lang="scss" scoped></style>
